import { useMemo, useState } from "react";
import type { EventId, PlanEvent, Task, TaskId } from "../../../shared/types";
import { getToday } from "../../../shared/types/date-helpers";
import { EventItem } from "../../events/components/event-item/EventItem";
import { TodayTaskSection } from "./TodayTaskSection";
import { CalendarIcon } from "../../../ui/icons";
import { EmptyState } from "../../../ui/atoms/EmptyState";
import { QuoteOfTheDay } from "./QuoteOfTheDay";
import { useCurrentUser } from "../../../lib/api/users.api";

interface TodayViewProps {
  readonly tasks: Task[];
  readonly events: PlanEvent[];
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onDeleteTask: (id: TaskId) => void;
  readonly onDeleteEvent: (id: EventId) => void;
  readonly onUpdateEvent?: (
    id: EventId,
    updates: Partial<
      Pick<
        PlanEvent,
        | "title"
        | "date"
        | "startTime"
        | "endTime"
        | "notes"
        | "description"
        | "tags"
        | "recurrence"
        | "reminderMinutes"
        | "category"
      >
    >,
  ) => void;
  readonly onQuickAdd?: () => void;
}

export function TodayView({
  tasks,
  events,
  onCycleStatus,
  onDeleteEvent,
  onUpdateEvent,
  onQuickAdd,
}: TodayViewProps) {
  const [showQuote, setShowQuote] = useState(true);
  const today = getToday();

  const todayEvents = useMemo(
    () =>
      events
        .filter((e) => e.date === today)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [events, today],
  );

  const doingTasks = useMemo(
    () => tasks.filter((t) => t.status === "doing" && !t.archived && !t.eventId),
    [tasks],
  );

  const todayDueTasks = useMemo(
    () =>
      tasks.filter(
        (t) =>
          t.status === "todo" &&
          !t.archived &&
          !t.eventId &&
          (!t.dueDate || t.dueDate <= today)
      ),
    [tasks, today],
  );

  const doneTasks = useMemo(
    () => tasks.filter((t) => t.status === "done" && !t.archived && !t.eventId),
    [tasks],
  );

  const { data: user } = useCurrentUser();

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    const base = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";
    return user?.name ? `${base}, ${user.name}` : base;
  }, [user]);

  const formattedDate = new Date(today + "T00:00:00").toLocaleDateString(
    "fr-FR",
    { weekday: "long", day: "numeric", month: "long" },
  );

  const isEmpty =
    todayEvents.length === 0 &&
    doingTasks.length === 0 &&
    todayDueTasks.length === 0 &&
    doneTasks.length === 0;

  return (
    <div className="flex flex-col gap-4 py-8">
      {/* Header compact */}
      <div className="flex items-center justify-between">
        <div>
          <span className="text-base font-medium text-ink">
            {greeting}
          </span>
          <span className="text-ink-muted mx-1">·</span>
          <span className="text-sm text-ink-muted">
            {formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)}
          </span>
        </div>
        <button
          onClick={() => setShowQuote(!showQuote)}
          className="text-xs text-ink-muted hover:text-ink transition-colors"
          title={showQuote ? "Masquer la citation" : "Afficher la citation"}
        >
          {showQuote ? "−" : "+"}
        </button>
      </div>

      {showQuote && <QuoteOfTheDay />}

      {todayEvents.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <h2 className="flex items-center gap-1.5 text-xs font-medium text-ink-muted uppercase tracking-wide">
            <CalendarIcon className="w-3 h-3" />
            Événements
          </h2>
          <div className="flex flex-col gap-1">
            {todayEvents.map((event) => (
              <EventItem
                key={event.id}
                event={event}
                onDelete={onDeleteEvent}
                onUpdate={onUpdateEvent || (() => {})}
              />
            ))}
          </div>
        </div>
      )}

      {doingTasks.length > 0 && (
        <TodayTaskSection
          tasks={doingTasks}
          title="En cours"
          onCycleStatus={onCycleStatus}
        />
      )}

      {todayDueTasks.length > 0 && (
        <TodayTaskSection
          tasks={todayDueTasks}
          title="À faire"
          onCycleStatus={onCycleStatus}
        />
      )}

      {doneTasks.length > 0 && (
        <TodayTaskSection
          tasks={doneTasks}
          title="Fait"
          onCycleStatus={onCycleStatus}
        />
      )}

      {isEmpty && (
        <EmptyState
          title="Journée libre"
          description="Planifiez votre journée"
          icon={
            <svg
              viewBox="0 0 24 24"
              className="w-12 h-12 text-ink-faint"
              stroke="currentColor"
              strokeWidth={2}
              fill="none"
            >
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          }
          action={
            onQuickAdd
              ? { label: "Ajouter une tâche", onClick: onQuickAdd }
              : undefined
          }
        />
      )}
    </div>
  );
}
